import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { Toast } from '@etnas/design-system';
import { describeError } from '@/api';

type ToastItem = { id: number; tone: 'success' | 'info' | 'critical'; title: string; body?: string };
interface ToastApi {
  success: (title: string, body?: string) => void;
  info: (title: string, body?: string) => void;
  error: (e: unknown, fallbackTitle?: string) => void;
}
const Ctx = createContext<ToastApi | null>(null);

/** Toast region: polite live region; critical errors also stay until dismissed by time (8 s). */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((t: Omit<ToastItem, 'id'>) => {
    const id = Date.now() + Math.random();
    setItems((xs) => [...xs.slice(-3), { ...t, id }]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), t.tone === 'critical' ? 8000 : 4500);
  }, []);
  const api = useMemo<ToastApi>(
    () => ({
      success: (title, body) => push({ tone: 'success', title, body }),
      info: (title, body) => push({ tone: 'info', title, body }),
      error: (e, fallbackTitle) => {
        const d = describeError(e);
        push({ tone: 'critical', title: fallbackTitle ?? d.title, body: [d.body, d.reference ? `Ref ${d.reference}` : ''].filter(Boolean).join(' · ') });
      },
    }),
    [push],
  );
  return (
    <Ctx.Provider value={api}>
      {children}
      <div className="ex-toasts" aria-live="polite" aria-relevant="additions">
        {items.map((t) => (
          <Toast key={t.id} tone={t.tone} title={t.title} body={t.body} />
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): ToastApi {
  const c = useContext(Ctx);
  if (!c) throw new Error('useToast outside ToastProvider');
  return c;
}
